import React, { useEffect, useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';

import { groupData } from '../utils/chart-helpers';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const WaitingRoomChart = ({ data, startDate, endDate, groupedBy, maxOutflow }) => {
    const [chartData, setChartData] = useState({
        labels: [],
        datasets: [],
    });
    const [yAxisMax, setYAxisMax] = useState(0);

    useEffect(() => {
        if (!data || !Array.isArray(data) || data.length === 0 || !startDate || !endDate || !groupedBy) {
            return;
        }

        const groupedData = groupData(data, groupedBy);
        const allTimestamps = Object.keys(groupedData).sort();
        const inflowData = allTimestamps.map((timestamp) => (
            groupedData[timestamp] ? groupedData[timestamp].inflow : 0
        ));
        const outflowData = allTimestamps.map((timestamp) => (
            groupedData[timestamp] ? groupedData[timestamp].outflow : 0
        ));
        const instantProcessedData = allTimestamps.map((timestamp) => (
            groupedData[timestamp] ? groupedData[timestamp].instantProcessed : 0
        ));
        const waitingUsersData = allTimestamps.map((timestamp) => (
            groupedData[timestamp] ? groupedData[timestamp].waitingUsers : 0
        ));
        const maxOutflowData = allTimestamps.map((timestamp) => (
            groupedData[timestamp] ? groupedData[timestamp].maxOutflow : 0
        ));

        const allValues = [
            ...inflowData, ...outflowData, ...instantProcessedData, ...waitingUsersData, ...maxOutflowData,
        ];
        const maxValueInAll = Math.max(...allValues);
        const maxWithBuffer = Math.ceil(maxOutflow * 1.25);
        const yAxisMaxValue = maxValueInAll > maxWithBuffer ? maxValueInAll : maxWithBuffer;

        setChartData({
            labels: allTimestamps,
            datasets: [
                {
                    label: 'Inflow',
                    data: inflowData,
                    borderColor: 'rgba(75, 192, 192, 1)',
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    fill: false,
                    tension: 0.4,
                    cubicInterpolationMode: 'monotone',
                },
                {
                    label: 'Outflow',
                    data: outflowData,
                    borderColor: 'rgba(255, 99, 132, 1)',
                    backgroundColor: 'rgba(255, 99, 132, 0.2)',
                    fill: false,
                    tension: 0.4,
                    cubicInterpolationMode: 'monotone',
                },
                {
                    label: 'Instant Processed',
                    data: instantProcessedData,
                    borderColor: 'rgba(255, 206, 86, 1)',
                    backgroundColor: 'rgba(255, 206, 86, 0.2)',
                    fill: false,
                    tension: 0.4,
                    cubicInterpolationMode: 'monotone',
                },
                {
                    label: 'Waiting Users',
                    data: waitingUsersData,
                    borderColor: 'rgba(153, 102, 255, 1)',
                    backgroundColor: 'rgba(153, 102, 255, 0.2)',
                    fill: false,
                    tension: 0.4,
                    cubicInterpolationMode: 'monotone',
                },
                {
                    label: 'Max Outflow',
                    data: maxOutflowData,
                    borderColor: 'rgba(255, 159, 64, 1)',
                    backgroundColor: 'rgba(255, 159, 64, 0.2)',
                    fill: false,
                    tension: 0.4,
                    cubicInterpolationMode: 'monotone',
                },
            ],
        });
        setYAxisMax(yAxisMaxValue);
    }, [data, startDate, endDate, groupedBy]);

    const isSingleDataPoint = chartData.labels.length === 1;

    return (
        <div style={{ width: '100%', height: '300px' }}>
            {chartData.labels.length > 0
                ? getBarOrChartComponent(chartData, yAxisMax, isSingleDataPoint)
                : <p>No data available to display.</p>
            }
        </div>
    );
};

const getBarOrChartComponent = (chartData, yAxisMax, isSingleDataPoint) => (
    <>
        {isSingleDataPoint
            ? <Bar
                data={chartData}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { position: 'top' },
                        title: { display: true, text: 'Waiting Room Statistics' },
                    },
                    scales: {
                        x: {},
                        y: { suggestedMax: yAxisMax },
                    },
                }}
            />
            : <Line
                data={chartData}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'top',
                            labels: {
                                usePointStyle: true,
                                generateLabels: (chart) => {
                                    const { datasets } = chart.data;
                                    return datasets.map((dataset, i) => ({
                                        text: dataset.label,
                                        datasetIndex: i,
                                        pointStyle: 'rect',
                                        fillStyle: dataset.borderColor,
                                        lineWidth: 0,
                                        hidden: !chart.isDatasetVisible(i),
                                    }));
                                },
                            },
                        },
                        tooltip: {
                            mode: 'index',
                            intersect: false,
                            callbacks: {
                                labelColor: (context) => ({
                                    backgroundColor: context.dataset.borderColor,
                                    borderWidth: 0,
                                }),
                            },
                        },
                    },
                    elements: { point: { radius: 0, hoverRadius: 4, hitRadius: 10 } },
                    scales: {
                        x: {
                            ticks: {
                                align: 'center',
                                autoSkip: true,
                                maxRotation: 0,
                                minRotation: 0,
                                maxTicksLimit: 6,
                            },
                        },
                        y: { suggestedMax: yAxisMax },
                    },
                }}
            />
        }
    </>
);

export default WaitingRoomChart;
